import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Check, X, ArrowRight, ShieldAlert } from "lucide-react";
import PageHero, { usePageMeta } from "@/components/marketing/PageHero";
import Reveal from "@/components/marketing/Reveal";
import { Button } from "@/components/ui/button";
import { getServicePage, getEligible, getNotSupported, getHowSteps } from "@/data/site";
import { useI18n } from "@/context/I18nContext";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const L = (it, en, de) => (lang === "en" ? en : lang === "de" ? (de ?? it) : it);
  const svc = getServicePage(slug, lang);
  usePageMeta(svc?.title || L("Servizio", "Service", "Dienstleistung"), svc?.intro);
  if (!svc) return <Navigate to="/servizi" replace />;
  const Icon = svc.icon;
  const steps = getHowSteps(lang);
  const eligible = getEligible(lang);
  const notSupported = getNotSupported(lang);

  return (
    <div>
      <PageHero eyebrow={L("Servizio", "Service", "Dienstleistung")} title={svc.title} subtitle={svc.intro}>
        <div className="mt-8">
          <Button asChild size="lg" className="bg-gold text-navy hover:bg-gold-hover" data-testid="service-detail-cta">
            <Link to="/avvia-recupero">{L("Richiedi una valutazione", "Request an assessment", "Bewertung anfragen")} <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
      </PageHero>

      <section className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-20">
        <div className="rounded-xl border border-gold/30 bg-gold/10 p-5 flex items-start gap-3 mb-12">
          <ShieldAlert className="h-5 w-5 text-gold shrink-0 mt-0.5" />
          <p className="text-sm text-gold">{L("Non inserire seed phrase complete, chiavi private, PIN o password complete in alcun modulo o messaggio.", "Do not enter full seed phrases, private keys, PINs or full passwords in any form or message.", "Geben Sie niemals vollständige Seed-Phrasen, private Schlüssel, PINs oder vollständige Passwörter in ein Formular oder eine Nachricht ein.")}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="h-full rounded-xl border border-white/10 bg-navy-paper p-6">
                <span className="font-heading text-4xl font-light text-cyanx/40">{s.n}</span>
                <h3 className="mt-2 font-heading text-lg font-medium text-white">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            <h3 className="font-heading text-xl font-medium text-white mb-4">{L("Casi potenzialmente valutabili", "Potentially assessable cases", "Möglicherweise bewertbare Fälle")}</h3>
            <ul className="space-y-2">
              {eligible.slice(0, 6).map((e) => (
                <li key={e} className="flex items-center gap-2.5 text-sm text-slate-300"><Check className="h-4 w-4 text-emerald-400 shrink-0" />{e}</li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="font-heading text-xl font-medium text-white mb-4">{L("Casi non supportati", "Unsupported cases", "Nicht unterstützte Fälle")}</h3>
            <ul className="space-y-2">
              {notSupported.slice(0, 5).map((e) => (
                <li key={e} className="flex items-center gap-2.5 text-sm text-slate-300"><X className="h-4 w-4 text-red-400 shrink-0" />{e}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
